/**
 * Discovery customer login page (Phase 4, 2026-05-22).
 *
 * Real email/password sign-in for customer tenants. Replaces the
 * demo-login screen as the default unauthenticated landing. The old
 * demo-login is preserved at `/login/demo` for development tenants.
 *
 * Tenant resolution:
 *   The login form derives tenantSlug from the current hostname
 *   (`<slug>.discovery.foctta.com`). On the apex (`discovery.foctta.com`)
 *   the form falls back to asking for a tenant slug explicitly - but
 *   normally customers arrive on their own subdomain, so the slug is
 *   automatic.
 *
 * Flow on success:
 *   - Backend returns `{ session: { token, expiresAt }, user }`
 *   - We call setAuth(...) to persist
 *   - Hard navigate to `/welcome` (same pattern as Phase A bridge -
 *     avoids the AppShell-mount-after-event race)
 */

import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, KeyRound } from 'lucide-react';

import { Mark } from '@/components/brand/Mark';
import {
  customerLoginApi,
  getCoreSsoConfig,
  getSsoDiscovery,
  setAuth,
  ssoLoginUrl,
  startCoreSso,
  type CoreSsoConfig,
  tenantApexFromHost,
  tenantSlugFromHost,
  type MfaSignInAgainReason,
} from '@/lib/api';
import {
  isMfaSignInAgainReason,
  MFA_ERROR_PARAM,
  MFA_SIGN_IN_AGAIN_COPY,
  mfaHandoffTarget,
  mfaNavigation,
} from './mfa-handoff';

/** Friendly copy for the ?sso_error= hint the login-initiate bounce sets. */
const SSO_ERROR_COPY: Record<string, string> = {
  not_configured:
    'Single sign-on isn’t set up for this workspace yet. Sign in with your email and password, or ask your administrator to configure SSO.',
  unknown_workspace: 'We couldn’t find that workspace. Check the name and try again.',
  bad_workspace: 'That workspace name isn’t valid. Check it and try again.',
};

export function Login() {
  const initialSlug = tenantSlugFromHost();
  const apex = tenantApexFromHost();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlugInput, setTenantSlugInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Workstream B (2026-07-12) - SSO affordance. `ssoKind` is set once we've
  // asked the server whether this tenant has an enabled IdP; the button only
  // appears when it does. Discovery never blocks the password path.
  const [ssoKind, setSsoKind] = useState<'saml' | 'oidc' | null>(null);
  // Release 3.1 - "Sign in with SSO" through FOCTTA Core, when this workspace
  // is paired with Core and inherits its sign-in. It takes the place of
  // Discovery's own SSO button: the workspace has chosen Core's sign-in.
  const [coreSso, setCoreSso] = useState<CoreSsoConfig | null>(null);
  const [coreSsoStarting, setCoreSsoStarting] = useState(false);
  const [ssoErrorCode] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('sso_error');
  });
  // Set by the MFA pages when the verification step could not finish (the
  // five-minute partial-auth token expired, the account was suspended, ...).
  // Only a known value shows anything; the copy is ours, never the URL's.
  const [mfaError] = useState<MfaSignInAgainReason | null>(() => {
    if (typeof window === 'undefined') return null;
    const value = new URLSearchParams(window.location.search).get(MFA_ERROR_PARAM);
    return isMfaSignInAgainReason(value) ? value : null;
  });

  const tenantSlug = initialSlug ?? tenantSlugInput.trim().toLowerCase();
  const onApex = initialSlug === null;

  // Ask the server whether this workspace has SSO, so we can show the button.
  // On a tenant subdomain the slug is fixed (runs once); on the apex the user
  // types it, so we debounce. Any failure leaves ssoKind null (password only).
  useEffect(() => {
    const slug = tenantSlug;
    if (!slug || !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(slug)) {
      setSsoKind(null);
      setCoreSso(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void getSsoDiscovery(slug).then((d) => {
        if (!cancelled) setSsoKind(d.ssoEnabled ? d.kind : null);
      });
      void getCoreSsoConfig(slug).then((c) => {
        if (!cancelled) setCoreSso(c.available ? c : null);
      });
    }, initialSlug ? 0 : 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tenantSlug, initialSlug]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email || !password || !tenantSlug) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const result = await customerLoginApi({ tenantSlug, email, password });
      // Wave 6 (2026-05-27) - MFA branch. The login endpoint returns
      // a partial-auth token instead of a full session when the user's
      // role requires MFA. Route to enrolment if they haven't yet set
      // up MFA, otherwise to challenge. The token rides in the fragment
      // (never sent to a server; crosses apex -> tenant host); the MFA page
      // scrubs it from the URL and sends it only as Authorization: Bearer
      // (mfa-handoff.ts).
      if ('mfaChallengeRequired' in result && result.mfaChallengeRequired) {
        const origin = onApex ? `https://${tenantSlug}.${apex}` : '';
        mfaNavigation.replace(mfaHandoffTarget(result.mfaReason, result.partialAuth.token, origin));
        return;
      }
      setAuth(result.session.token, result.user, result.session.expiresAt);
      // Hard navigate so AppShell remounts with the new auth state.
      // 2026-05-27 - land on Home (/) not /welcome. Welcome is now
      // a settings-area checklist, not the post-login landing page.
      const target = onApex
        ? `https://${tenantSlug}.${apex}/`
        : '/';
      window.location.replace(target);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Sign-in failed.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-4 mb-8">
          <Mark variant="wordmark" size={44} />
          <h1 className="text-2xl font-bold tracking-tight">Sign in to your workspace</h1>
          {tenantSlug && !onApex ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-mono">{tenantSlug}.{apex}</span>
            </p>
          ) : null}
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-5 bg-white dark:bg-slate-900 rounded-lg shadow-sm border p-8"
        >
          {mfaError ? (
            <div
              role="status"
              className="flex gap-2 items-start p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-sm text-amber-800 dark:text-amber-300"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{MFA_SIGN_IN_AGAIN_COPY[mfaError]}</span>
            </div>
          ) : null}
          {ssoErrorCode ? (
            <div className="flex gap-2 items-start p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-sm text-amber-800 dark:text-amber-300">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{SSO_ERROR_COPY[ssoErrorCode] ?? 'Single sign-on couldn’t start. Sign in with your email and password instead.'}</span>
            </div>
          ) : null}

          {onApex ? (
            <div>
              <label htmlFor="tenantSlug" className="block text-sm font-medium mb-1">
                Workspace
              </label>
              <div className="flex items-center gap-2 rounded-md border border-input bg-background pl-3 pr-3">
                <input
                  id="tenantSlug"
                  type="text"
                  required
                  pattern="[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"
                  value={tenantSlugInput}
                  onChange={(e) =>
                    setTenantSlugInput(
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    )
                  }
                  className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
                  placeholder="your-workspace"
                  autoComplete="organization"
                />
                <span className="text-sm text-muted-foreground">.{apex}</span>
              </div>
            </div>
          ) : null}

          {coreSso ? (
            <>
              <button
                type="button"
                data-testid="core-sso-button"
                disabled={coreSsoStarting}
                onClick={() => {
                  setCoreSsoStarting(true);
                  setErrorMsg(null);
                  void startCoreSso(tenantSlug, coreSso, '/')
                    .then((url) => {
                      window.location.href = url;
                    })
                    .catch((err: unknown) => {
                      setCoreSsoStarting(false);
                      setErrorMsg(err instanceof Error ? err.message : 'Sign-in through FOCTTA Core could not start.');
                    });
                }}
                className="w-full inline-flex flex-col justify-center items-center gap-0.5 rounded-md border border-input bg-background px-4 py-3 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
              >
                <span className="inline-flex items-center gap-2">
                  {coreSsoStarting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  Sign in with SSO
                </span>
                <span className="text-xs font-normal text-muted-foreground">
                  Through {coreSso.consentName ?? 'FOCTTA Core'}, the same sign-in you use there
                </span>
              </button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden>
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-slate-900 px-2 text-muted-foreground">
                    or continue with email
                  </span>
                </div>
              </div>
            </>
          ) : ssoKind ? (
            <>
              <button
                type="button"
                onClick={() => {
                  window.location.href = ssoLoginUrl(tenantSlug, '/');
                }}
                className="w-full inline-flex justify-center items-center gap-2 rounded-md border border-input bg-background px-4 py-3 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground"
              >
                <KeyRound className="h-4 w-4" />
                Sign in with SSO
              </button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden>
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-slate-900 px-2 text-muted-foreground">
                    or continue with email
                  </span>
                </div>
              </div>
            </>
          ) : null}

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              autoComplete="email"
              autoFocus={!onApex}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              autoComplete="current-password"
            />
            <div className="mt-2 text-right">
              <a
                href="/forgot-password"
                className="text-xs text-muted-foreground hover:text-primary"
              >
                Forgot your password?
              </a>
            </div>
          </div>

          {errorMsg ? (
            <div className="flex gap-2 items-start p-3 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting || !email || !password || !tenantSlug}
            className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </button>

          <p className="text-xs text-muted-foreground text-center pt-2">
            Don't have an account?{' '}
            <a href="https://app.foctta.com/signup" className="text-primary hover:underline">
              Get started with FOCTTA
            </a>
          </p>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          FOCTTA Discovery · DSPM · DAM · DDR · Compliance
        </p>
      </div>
    </div>
  );
}

export default Login;
